import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Paper, CircularProgress, LinearProgress } from '@mui/material';

import { redirectToLogout } from '../utils/backendUrl';
import { authService } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

const RETRY_INTERVAL_SECONDS = 30;
const DEFAULT_MESSAGE = 'Your account has not yet been approved.';
const DEFAULT_SUGGESTION = 'Please contact your administrator to approve your request.';

const PendingApprovalPage: React.FC = () => {
  const { pendingApprovalMessage, pendingApprovalSuggestion, setPendingApprovalDetails } = useAuth();
  const [checking, setChecking] = useState(false);
  const [countdown, setCountdown] = useState(RETRY_INTERVAL_SECONDS);
  const message = pendingApprovalMessage ?? DEFAULT_MESSAGE;
  const suggestion = pendingApprovalSuggestion ?? DEFAULT_SUGGESTION;

  const checkApproval = useCallback(async () => {
    setChecking(true);
    try {
      // Force token refresh to get updated Cognito groups after admin approval
      const result = await authService.getSession(true);
      if (result.pendingApproval) {
        setPendingApprovalDetails(result.message ?? null, result.suggestion ?? null);
      } else if (result.user) {
        // User has been approved - reload to get full access
        window.location.reload();
      }
    } catch {
      // Ignore errors, will retry
    } finally {
      setChecking(false);
      setCountdown(RETRY_INTERVAL_SECONDS);
    }
  }, [setPendingApprovalDetails]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          checkApproval();
          return RETRY_INTERVAL_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [checkApproval]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default'
      }}
    >
      <Paper sx={{ p: 0, maxWidth: 400, textAlign: 'center', overflow: 'hidden' }}>
        <Box sx={{ px: 4 }}>
          <Typography sx={{ mt: 2, fontSize: '1.5rem', fontWeight: 700 }}>
            Claim Assistant
          </Typography>
          <Typography variant="h5" gutterBottom>
            Account Pending Approval
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            {message}
          </Typography>
          <Typography color="text.secondary" >
            {suggestion}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            {checking ? (
              <>
                <CircularProgress size={16} />
                Checking approval status...
              </>
            ) : (
              <>Checking again in {countdown}s</>
            )}
          </Typography>
          <Button variant="outlined" onClick={() => redirectToLogout()} sx={{ mt: 2, mb: 2 }}>
            Sign Out
          </Button>
        </Box>
        <LinearProgress
          variant={checking ? 'indeterminate' : 'determinate'}
          value={checking ? undefined : ((RETRY_INTERVAL_SECONDS - countdown) / RETRY_INTERVAL_SECONDS) * 100}
          sx={{
            height: 4,
            '& .MuiLinearProgress-bar': {
              transition: 'transform 1s linear'
            }
          }}
        />
      </Paper>
    </Box>
  );
};

export default PendingApprovalPage;
